import moment, { Moment } from 'moment';
import { AbsoluteValue } from '../constants/typedef';
import { DateType } from '../models/date';
import { ErrorWithError, ErrorWithErrorsArray, ErrorWithMessage } from '../models/error/error';
import { Stories } from '../models/stories';
type AnyObject = { [key: string]: any };

const utilsService = {
    isEqual,
    isBirthdateValid,
    convertUTCDateToLocalDate,
    convertLocalDateToUTCDate,
    constructUrlWithParams,
    getDateTimeString,
    getDateString,
    getDate,
    validateEmail,
    validatePhoneNumber,
    isTodayWithinDateRange,
    isBeforeToday,
    formatNumber,
    initTransition,
    getCurrentStory,
    decamelize,
    replaceNumberInput,
    toCamelCase,
    appendToFormData,
    getErrorMessage,
    getSocialLoginErrorMessage,
    checkHasAnyNotEmptyObj,
    checkHasAnyEmptyObj,
    toMoment,
    toDate,
    formatDay,
    checkIsAnyEmptyKindOfData,
    checkIfAnyTwoStringFieldsHaveValue,
    checkFirstEmptyField,
    handleShareClick,
    getLastStep,
    isEmpty,
    cleanObject,
    filterChangedData,
    detectYouTubeUrl,
    hasKey,
    hasKeys,
    convertToMB,
    getAppDomain,
    normalizeDomain,
    getS3ImageUrl,
    isUrl,
    toSnakeCase,
    flexibleFormatPhoneNumber,
    cleanHTML,
    stripHtml,
    isSafeRedirectPath,
    buildLoginUrl
};

function stripHtml(html: string | null | undefined): string {
    if (!html) return '';
    return html.replace(/<[^>]*>?/gm, '');
}

// U6-6: 로그인 후 되돌아갈 경로(쿼리로 전달됨)가 우리 도메인 내부 경로인지 확인한다.
// "//evil.com" 같은 프로토콜 상대 경로로 열린 리다이렉트가 되지 않도록 막는다.
function isSafeRedirectPath(path: string | null | undefined): path is string {
    return !!path && path.startsWith('/') && !path.startsWith('//');
}

// U6-6: 로그인이 필요해 화면 밖으로 보낼 때 지금 있던 경로를 붙인다. 로그인 뒤 이 경로로
// 돌아온다(AuthGuard·Login 이 redirect 쿼리를 읽는다). /login 자신으로의 루프는 만들지 않는다.
function buildLoginUrl(currentPath?: string | null): string {
    if (!isSafeRedirectPath(currentPath) || currentPath === '/login' || currentPath.startsWith('/login?')) {
        return '/login';
    }
    return `/login?redirect=${encodeURIComponent(currentPath)}`;
}

export const MIN_DATE_TIME = new Date(1970, 0, 1, 0, 0, 0);
const BASE_URL = process.env.BASE_URL;

function hasKey<T extends object>(obj: T, key: keyof any): boolean {
    return key in obj;
}

function flexibleFormatPhoneNumber(phoneNumber: string): string {
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length <= 6) {
        return cleaned;
    }
    let result = '';
    let index = 0;
    result += cleaned.substring(index, index + 3);
    index += 3;
    while (index < cleaned.length) {
        const nextGroupLength = cleaned.length - index > 4 ? 4 : cleaned.length - index;
        result += '-' + cleaned.substring(index, index + nextGroupLength);
        index += nextGroupLength;
    }
    return result;
}

function getAppDomain(): string {
    if (typeof window !== 'undefined') {
        return normalizeDomain(window.location.hostname);
    }
    return process.env.NEXT_PUBLIC_APP_DOMAIN || 'joysamconnect.com';
}

function isUrl(url: string): boolean {
    return /https?:\/\/[^\s/$.?#].[^\s]*/.test(url);
}

function convertToMB(bytes: number) {
    const megabytes = bytes / (1024 * 1024);
    return megabytes.toFixed(2);
}

/**
 * Strips known subdomain prefixes (www, stage, dev, etc.) to get the base domain.
 * e.g. stage.joysamconnect.com → joysamconnect.com
 *      www.joysamconnect.com   → joysamconnect.com
 *      joysamconnect.com       → joysamconnect.com
 *      localhost               → localhost
 */
function normalizeDomain(domain: string): string {
    return domain.replace(/^(www|stage|dev|stg|staging)\./, '');
}

function hasKeys<T extends object>(obj: T, keys: (keyof T)[]): boolean {
    return keys.every((key) => key in obj);
}
function filterChangedData(newData: any, existingData: any) {
    const changedData: any = {};

    for (const key in newData) {
        if (Array.isArray(newData[key]) && Array.isArray(existingData[key])) {
            const arrayChanges = newData[key].filter((item: any, index: number) => !utilsService.isEqual(item, existingData[key][index]));
            if (arrayChanges.length > 0) {
                changedData[key] = arrayChanges;
            }
        } else if (typeof newData[key] === 'object' && typeof existingData[key] === 'object') {
            const objectChanges = filterChangedData(newData[key], existingData[key] ?? {});
            if (Object.keys(objectChanges).length > 0) {
                changedData[key] = objectChanges;
            }
        } else if (!utilsService.isEqual(newData[key], existingData[key])) {
            changedData[key] = newData[key];
        }
    }

    return changedData;
}

function formatNumber(value: number): string {
    if (value?.toString() && typeof value === 'number') {
        return value.toLocaleString('en-US');
    }
    return '0';
}

function detectYouTubeUrl(url: string): boolean {
    const youTubeRegex =
        /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})([?&].*)?$/;
    return youTubeRegex.test(url);
}

function getDate(utcDateTime: string): Date {
    //'yyyy-MM-dd HH:mm:ss'
    if (utcDateTime.length === 19 && utcDateTime[10] === ' ') {
        const curDate = utcDateTime.substring(0, 10) + 'T' + utcDateTime.substring(11, 19) + '.000Z';
        return new Date(curDate);
    }

    if (utcDateTime.length > AbsoluteValue.Number) {
        return new Date(utcDateTime);
    }

    return MIN_DATE_TIME;
}

function replaceNumberInput(value: string): any {
    const regex = /[^0-9.]/g;
    return value.replace(regex, '');
}

function getLastStep(stepObject: any) {
    const keys = Object.keys(stepObject);

    if (keys.length === 0) return null;

    const lastKey = keys[keys.length - 1];

    const lastStepObject = stepObject[lastKey];

    if (typeof lastStepObject === 'object' && lastStepObject !== null) {
        return getLastStep(lastStepObject);
    }

    return lastStepObject;
}

function checkIsAnyEmptyKindOfData(data: any): boolean {
    const requiredFields = Object.keys(data);
    for (const field of requiredFields) {
        if (Array.isArray(data[field]) && data[field].length === 0) {
            return false;
        } else if (typeof data[field] === 'object' && data[field] !== null && !Object.keys(data[field]).length) {
            return false;
        } else if (data[field] === null || data[field] === undefined || (typeof data[field] === 'string' && data[field].length === 0)) {
            return false;
        }
    }
    return true;
}

function isEmpty(value: any): boolean {
    return (
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && !Array.isArray(value) && !(value instanceof File) && Object.keys(value).length === 0)
    );
}

function cleanObject(obj: any): any {
    if (Array.isArray(obj)) {
        return obj.map(cleanObject).filter((item) => !isEmpty(item));
    } else if (typeof obj === 'object' && obj !== null) {
        if (obj instanceof File) {
            return obj; // Keep File objects as they are
        }
        return Object.entries(obj).reduce((acc, [key, value]) => {
            const cleanedValue = cleanObject(value);
            if (!isEmpty(cleanedValue)) {
                acc[key] = cleanedValue;
            }
            return acc;
        }, {} as AnyObject);
    }
    return obj;
}

function checkFirstEmptyField(data: { [key: string]: any }): string | null {
    const requiredFields = Object.keys(data);
    for (const field of requiredFields) {
        if (Array.isArray(data[field]) && data[field].length === 0) {
            return field;
        } else if (typeof data[field] === 'object' && data[field] !== null && !Object.keys(data[field]).length) {
            return field;
        } else if (data[field] === null || data[field] === undefined || (typeof data[field] === 'string' && data[field].length === 0)) {
            return field;
        }
    }
    return null;
}

function checkIfAnyTwoStringFieldsHaveValue(data: any): boolean {
    const requiredFields = Object.keys(data);
    let nonEmptyFieldCount = 0;

    for (const field of requiredFields) {
        if (typeof data[field] === 'string' && data[field].length > 0) {
            nonEmptyFieldCount++;
        }

        if (nonEmptyFieldCount >= 2) {
            return true;
        }
    }

    return false;
}

function formatDay(date: Moment, locale = 'en') {
    return moment(date).locale(locale).format('ddd');
}

function isBeforeToday(startDate: DateType): boolean {
    if (!startDate) return true;
    const start = toMoment(startDate);
    const today = moment();
    return start ? (start.isBefore(today) || start.isSame(today)) : true;
}

function isTodayWithinDateRange(startDate: DateType, endDate: DateType): boolean {
    const today = moment();
    const start = toMoment(startDate);
    const end = toMoment(endDate);

    if (!start && !end) {
        return true;
    }

    if (!start) {
        return today.isSameOrBefore(end);
    }

    if (!end) {
        return today.isSameOrAfter(start);
    }

    return today.isBetween(start, end, undefined, '[]');
}

function convertUTCDateToLocalDate(date: DateType): Date | null {
    const momentDate = toMoment(date);
    if (!momentDate) {
        return null;
    }
    return momentDate.local().toDate();
}

function convertLocalDateToUTCDate(date: DateType): Date | null {
    const momentDate = toMoment(date);
    if (!momentDate) {
        return null;
    }
    return momentDate.utc().toDate();
}

function getDateTimeString(dateTime: DateType): string {
    const momentDate = toMoment(dateTime);
    if (!momentDate) {
        return '';
    }
    return momentDate.format('YYYY-MM-DD HH:mm:ss');
}

function getDateString(dateTime: DateType): string {
    const momentDate = toMoment(dateTime);
    if (!momentDate) {
        return '';
    }
    return momentDate.format('YYYY-MM-DD');
}

function validateEmail(email: string): boolean {
    // eslint-disable-next-line no-useless-escape
    return /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);
}

function validatePhoneNumber(phoneNumber: string): boolean {
    //+(123) - 456-78-90
    // eslint-disable-next-line no-useless-escape
    return /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/.test(phoneNumber);
}

function decamelize(str: string): string {
    return str.replace(/([a-z\d])([A-Z])/g, '$1_$2').toLowerCase();
}

function toCamelCase(obj: any, options: { deep?: boolean } = {}): any {
    if (Array.isArray(obj)) {
        return obj.map((v) => (options.deep ? toCamelCase(v, options) : v));
    } else if (obj !== null && obj.constructor === Object) {
        return Object.keys(obj).reduce((result: any, key: string) => {
            const camelKey = key.replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''));
            result[camelKey] = options.deep ? toCamelCase(obj[key], options) : obj[key];
            return result;
        }, {});
    }
    return obj;
}

function toSnakeCase(obj: any, options: { deep?: boolean } = {}): any {
    if (Array.isArray(obj)) {
        return obj.map((v) => (options.deep ? toSnakeCase(v, options) : v));
    } else if (obj !== null && obj.constructor === Object) {
        return Object.keys(obj).reduce((result: any, key: string) => {
            const snakeKey = key.replace(/([A-Z])/g, (group) => `_${group.toLowerCase()}`);
            result[snakeKey] = options.deep ? toSnakeCase(obj[key], options) : obj[key];
            return result;
        }, {});
    }
    return obj;
}

async function appendToFormData<T extends object>(data: T, useDecamelize: boolean = true): Promise<FormData> {
    const formData = new FormData();
    const logs: string[] = [];

    function processFormData(key: string, value: any) {
        const isFile = value instanceof File;
        const isBlob = value instanceof Blob;
        if (key.includes('media') || key.includes('file') || value instanceof File || value instanceof Blob || (value !== null && typeof value === 'object')) {
            logs.push(`${key}: type=${typeof value}, isFile=${isFile}, isBlob=${isBlob}`);
        }
        console.log(`[appendToFormData] Key: "${key}", Type: ${typeof value}, isFile: ${value instanceof File}, isBlob: ${value instanceof Blob}`);
        if (value instanceof File) {
            formData.append(key, value);
        } else if (Array.isArray(value)) {
            value.forEach((item, index) => {
                processFormData(`${key}[${index}]`, item);
            });
        } else if (value !== null && typeof value === 'object') {
            Object.keys(value).forEach((subKey) => {
                const newKey = useDecamelize ? decamelize(subKey) : subKey;
                processFormData(`${key}[${newKey}]`, value[subKey]);
            });
        } else {
            formData.append(key, value != null ? (typeof value == 'boolean' ? value : value.toString()) : '');
        }
    }

    Object.keys(data).forEach((key) => {
        const value = (data as any)[key];
        const newKey = useDecamelize ? decamelize(key) : key;
        processFormData(newKey, value);
    });

    return formData;
}

const isErrorWithMessage = (error: any): error is ErrorWithMessage => {
    return error && typeof error?.message === 'string';
};

const isErrorWithError = (error: any): error is ErrorWithError => {
    return error && typeof error?.error === 'string';
};

const isErrorFor422Code = (error: any): error is ErrorWithErrorsArray => {
    return error?.errors && typeof error.errors === 'object' && Object.keys(error.errors)?.length > 0;
};

// 소셜 로그인 실패 시 "Firebase: Error (auth/xxx)." 원문이 그대로 토스트에 뜨던 것을 사람 말로 바꾼다.
// signInWithOAuth/merge-method 양쪽 다 실패 시 원본 Firebase 에러를 { error: FirebaseError } 형태로
// 감싸서 던지므로, 몇 단계 안까지 code 를 찾아 매칭한다.
const FIREBASE_AUTH_ERROR_MESSAGES: Record<string, string> = {
    'auth/popup-closed-by-user': '로그인 창이 닫혔습니다. 다시 시도해 주세요.',
    'auth/cancelled-popup-request': '로그인 창이 닫혔습니다. 다시 시도해 주세요.',
    'auth/popup-blocked': '팝업이 차단되었습니다. 브라우저의 팝업 차단을 해제한 뒤 다시 시도해 주세요.',
    'auth/network-request-failed': '네트워크 연결을 확인한 뒤 다시 시도해 주세요.',
    'auth/account-exists-with-different-credential': '이미 다른 방법으로 가입된 계정입니다. 다른 로그인 방법으로 시도해 주세요.',
    'auth/user-disabled': '이용이 제한된 계정입니다. 고객센터에 문의해 주세요.',
    'auth/too-many-requests': '잠시 후 다시 시도해 주세요.',
};

function findFirebaseAuthCode(error: any, depth: number = 0): string | null {
    if (!error || depth > 3) return null;
    if (typeof error.code === 'string' && error.code.startsWith('auth/')) return error.code;
    return findFirebaseAuthCode(error.error, depth + 1);
}

function getSocialLoginErrorMessage(error: any): string | null {
    const code = findFirebaseAuthCode(error);
    if (!code) return null;
    return FIREBASE_AUTH_ERROR_MESSAGES[code] ?? '로그인에 실패했습니다. 다시 시도해 주세요.';
}

function getErrorMessage(error: any): string | any {
    if (!error?.data?.status && error?.code !== 'ERR_CANCELED' && error?.code === 'ERR_NETWORK') {
        return '';
    }
    if (error?.status == 410) {
        return error?.msg ?? error?.message;
    }
    if (error?.status == 403) {
        return error?.msg ?? error?.message;
    }
    if (isErrorFor422Code(error)) {
        // 라라벨 검증 오류는 errors.필드 = [문구, ...] 배열이다. 우리 거절(HandleException)은
        // errors.code = 'cash_below_minimum' 같은 문자열이라 [0] 을 하면 첫 글자만 나온다(JOYSAM-113).
        const first = error.errors[Object.keys(error.errors)[0]];
        if (Array.isArray(first) && typeof first[0] === 'string') {
            return first[0];
        }
    }
    if (isErrorWithMessage(error)) {
        return error?.message;
    } else if (isErrorWithError(error)) {
        return error.error;
    } else {
        return error;
    }
}

function checkHasAnyNotEmptyObj(object: Object): boolean {
    return Object.values(object).some((error) => error !== null && error !== undefined && error !== '');
}

function checkHasAnyEmptyObj(object: Object): boolean {
    return Object.values(object).some((value) => {
        return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0) || (typeof value === 'object' && Object.keys(value).length === 0);
    });
}

function toMoment(date: DateType): Moment | null {
    if (!date) {
        return null;
    }
    return moment(date);
}

function toDate(date: DateType): Date | null {
    if (!date) {
        return null;
    }
    return moment(date).toDate();
}

function handleShareClick(text: any, callback: () => void): void {
    navigator.clipboard.writeText(text).then(
        () => {
            callback();
        },
        (err) => {
            console.error('Could not copy text: ', err);
        }
    );
}

export default utilsService;

export function detectDeviceClient() {
    if (typeof window === 'undefined') {
        return 'unknown'; // Server-side rendering situation
    }

    const ua = window.navigator.userAgent.toLowerCase();

    if (/android/i.test(ua)) {
        return 'android';
    }

    if (/iphone|ipad|ipod/i.test(ua)) {
        return 'ios';
    }

    if (/win|mac|linux|x11/i.test(ua)) {
        return 'pc';
    }

    return 'unknown';
}

function isEqual(obj1: any, obj2: any): boolean {
    if (obj1 === obj2) return true;

    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
        return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1?.length !== keys2?.length) return false;

    for (const key of keys1) {
        if (!keys2.includes(key) || !isEqual(obj1[key], obj2[key])) {
            return false;
        }
    }

    return true;
}

function isBirthdateValid(birthdate: Moment | string | null): boolean {
    const today = moment().startOf('day');

    if (!birthdate) {
        return false;
    }

    let birthdateMoment;

    if (moment.isMoment(birthdate)) {
        birthdateMoment = birthdate.startOf('day');
    } else {
        birthdateMoment = moment(birthdate, 'YYYY-MM-DD').startOf('day');
    }

    return birthdateMoment.isSameOrAfter(today);
}

function constructUrlWithParams(basePath: string, params: Record<string, string | number | null | undefined>): string {
    const url = new URL(basePath, BASE_URL);

    Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            url.searchParams.append(key, String(value));
        }
    });

    return url.toString();
}

function initTransition(spanId: string) {
    const spanTransition = document.getElementById(spanId);
    spanTransition?.classList.add('story-hover-transition');
}

function getCurrentStory(stories: Stories[], current: Stories) {
    const currentStoryIndex = stories?.findIndex((story) => story.id === current.id);
    return {
        currentStoryIndex,
        currentStory: stories?.[currentStoryIndex],
    };
}

function cleanHTML(html: string) {
    return html.replace(/<p>(&nbsp;|\s*)<\/p>/g, '');
}

/**
 * Convert relative S3 path to absolute URL
 * @param path - Relative path or absolute URL
 * @returns Absolute URL with S3 domain
 */
function getS3ImageUrl(path: string | null | undefined): string {
    if (!path) return '';

    // Already absolute URL
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }

    // Relative path - convert to S3 URL
    const protocol = process.env.NEXT_PUBLIC_IMAGE_PROTOCOL || 'https';
    const hostname = process.env.NEXT_PUBLIC_IMAGE_HOSTNAME || 'joysam-dev.s3.ap-southeast-1.amazonaws.com';

    // Remove leading slash if exists
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;

    return `${protocol}://${hostname}/${cleanPath}`;
}
